// # FILE: src/app/(portal)/guard/login/guard-login-form.tsx
// # VERSION: 1.0.0
// # PURPOSE: Client-форма входа охранника: email+пароль (Supabase Auth) + кнопка
// #   «Войти через Госуслуги (ЕСИА)». ЕСИА-кнопка обращается к /api/auth/esia/start
// #   (server строится authorizeUrl + httpOnly state-cookie) и редиректит на Госуслуги.
// # SCOPE: client component.
// # INPUT: props.esiaEnabled.
// # OUTPUT: вход → редирект /guard.
// # KEYWORDS: [DOMAIN(9): B2C-Passport; CONCEPT(8): ESIA-KYC; TECH(7): Auth]
// # LINKS: READS_DATA_FROM(auth.session); WRITES_DATA_TO(auth.session)
// # Дата: 2026-08-31

"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Loader2, Eye, EyeOff, Landmark } from "lucide-react";
import { createClient } from "@oxpa/shared/lib/supabase/client";
import { Button } from "@oxpa/shared/ui/button";
import { usePortalSession } from "@/features/seo-portal/components/portal-session-provider";

export function GuardLoginForm({ esiaEnabled }: { esiaEnabled: boolean }) {
  const router = useRouter();
  const { user, isLoading } = usePortalSession();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [esiaLoading, setEsiaLoading] = useState(false);
  const [error, setError] = useState("");
  const [esiaNote, setEsiaNote] = useState("");

  // Если уже залогинен — уводим в кабинет охранника (middleware тоже это делает).
  useEffect(() => {
    if (!isLoading && user) router.replace("/guard");
  }, [isLoading, user, router]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const supabase = createClient();
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) {
        setError(
          signInError.message === "Invalid login credentials"
            ? "Неверный email или пароль"
            : signInError.message
        );
        return;
      }
      router.push("/guard");
      router.refresh();
    } catch {
      setError("Произошла непредвиденная ошибка. Попробуйте позже.");
    } finally {
      setLoading(false);
    }
  }

  async function handleEsia(e: React.MouseEvent) {
    e.preventDefault();
    setError("");
    setEsiaNote("");
    if (!esiaEnabled) {
      setEsiaNote(
        "Вход через Госуслуги ещё не подключён. Используйте email и пароль — личность можно будет подтвердить позже."
      );
      return;
    }
    setEsiaLoading(true);
    try {
      const res = await fetch("/api/auth/esia/start");
      const data = await res.json();
      if (data.configured && data.authorizeUrl) {
        window.location.href = data.authorizeUrl;
      } else {
        setEsiaNote(
          "Вход через Госуслуги ещё не подключён. Используйте email и пароль."
        );
        setEsiaLoading(false);
      }
    } catch {
      setEsiaNote("Не удалось запустить вход через Госуслуги. Попробуйте позже.");
      setEsiaLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-[10px] text-[hsl(var(--portal-text-muted))] uppercase tracking-widest mb-2">
          Электронная почта
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="guard@example.ru"
          required
          className="w-full bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] px-4 py-3 font-sans text-sm focus:outline-none focus:border-[hsl(var(--portal-accent))]/50 transition-colors"
        />
      </div>

      <div>
        <label className="block text-[10px] text-[hsl(var(--portal-text-muted))] uppercase tracking-widest mb-2">
          Пароль
        </label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="******"
            required
            minLength={6}
            className="w-full bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] placeholder:text-[hsl(var(--portal-text-dim))] px-4 py-3 pr-10 font-sans text-sm focus:outline-none focus:border-[hsl(var(--portal-accent))]/50 transition-colors"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] transition-colors focus:outline-none"
            aria-label="Показать/скрыть пароль"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {error && (
        <div className="border border-[hsl(var(--portal-danger))]/30 bg-[hsl(var(--portal-danger))]/10 text-[hsl(var(--portal-danger))] text-sm font-sans px-4 py-3">
          {error}
        </div>
      )}

      <Button
        type="submit"
        disabled={loading}
        className="w-full rounded-none h-14 bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent))] uppercase tracking-[0.15em] text-sm font-bold disabled:opacity-50"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
            Вход...
          </>
        ) : (
          "Войти"
        )}
      </Button>

      {/* Разделитель */}
      <div className="flex items-center gap-3 my-2">
        <span className="flex-1 h-px bg-[hsl(var(--portal-surface-2))]" />
        <span className="text-[10px] text-[hsl(var(--portal-text-dim))] uppercase tracking-widest">или</span>
        <span className="flex-1 h-px bg-[hsl(var(--portal-surface-2))]" />
      </div>

      {/* ЕСИА-вход */}
      <Button
        type="button"
        onClick={handleEsia}
        disabled={esiaLoading}
        variant="outline"
        aria-disabled={!esiaEnabled}
        className="w-full rounded-none h-14 border border-[hsl(var(--portal-border-hover))] text-[hsl(var(--portal-text))] hover:bg-[hsl(var(--portal-surface))] bg-transparent uppercase tracking-[0.12em] text-sm font-bold disabled:opacity-60"
      >
        {esiaLoading ? (
          <>
            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
            Переход на Госуслуги...
          </>
        ) : (
          <>
            <Landmark className="mr-2 w-4 h-4" />
            Войти через Госуслуги
          </>
        )}
      </Button>

      {esiaNote && (
        <p className="text-xs text-[hsl(var(--portal-text-muted))] font-sans leading-relaxed">{esiaNote}</p>
      )}

      <div className="mt-6 pt-6 border-t border-[hsl(var(--portal-border))] text-center">
        <p className="text-[10px] text-[hsl(var(--portal-text-dim))] uppercase tracking-widest font-sans">
          Нет аккаунта?{" "}
          <Link
            href="/register"
            className="text-[hsl(var(--portal-accent))] hover:text-[hsl(var(--portal-accent))] transition-colors underline underline-offset-4 decoration-[hsl(var(--portal-accent))]/30"
          >
            Зарегистрироваться
          </Link>
        </p>
      </div>
    </form>
  );
}
